import React from "react"

import type Prism from "prismjs"

interface TextSpan {
  className?: string
  text: string
}

const PUNCTUATION_CLASS_NAME = "token punctuation"

const isQuotedJsonTokenClass = (className: string): boolean => {
  const tokenType = className.split(" ").at(-1)

  return tokenType === "property" || tokenType === "string"
}

const wrappingQuoteSpans = (
  text: string,
  className: string
): TextSpan[] | null => {
  if (
    !isQuotedJsonTokenClass(className) ||
    text.length < 2 ||
    !text.startsWith('"') ||
    !text.endsWith('"')
  ) {
    return null
  }

  const inner = text.slice(1, -1)
  const quoteSpan: TextSpan = {
    className: PUNCTUATION_CLASS_NAME,
    text: '"',
  }

  if (inner.length === 0) {
    return [quoteSpan, quoteSpan]
  }

  return [quoteSpan, { className, text: inner }, quoteSpan]
}

const pushSpan = (
  lines: TextSpan[][],
  text: string,
  className?: string
): void => {
  const parts = text.split("\n")

  for (let partIndex = 0; partIndex < parts.length; partIndex++) {
    if (partIndex > 0) {
      lines.push([])
    }

    const part = parts[partIndex]
    if (part && part.length > 0) {
      lines
        .at(-1)
        ?.push(
          className === undefined ? { text: part } : { className, text: part }
        )
    }
  }
}

const walkToken = (
  token: string | Prism.Token | (string | Prism.Token)[],
  lines: TextSpan[][],
  className?: string
): void => {
  if (typeof token === "string") {
    const quotedSpans =
      className === undefined ? null : wrappingQuoteSpans(token, className)
    if (quotedSpans !== null) {
      for (const span of quotedSpans) {
        pushSpan(lines, span.text, span.className)
      }
      return
    }

    pushSpan(lines, token, className)
    return
  }

  if (Array.isArray(token)) {
    for (const child of token) {
      walkToken(child, lines, className)
    }
    return
  }

  const nextClassName =
    className === undefined
      ? `token ${token.type}`
      : `${className} ${token.type}`
  walkToken(token.content, lines, nextClassName)
}

export const tokenizeJsonLines = (
  PrismApi: typeof Prism,
  prettyJson: string
): React.ReactNode[][] => {
  const grammar = PrismApi.languages.json
  if (!grammar) {
    return prettyJson.split("\n").map((line) => [line])
  }

  const tokens = PrismApi.tokenize(prettyJson, grammar)
  const spanLines: TextSpan[][] = [[]]
  walkToken(tokens, spanLines)

  return spanLines.map((spans, lineIndex) =>
    spans.map((span, spanIndex) => {
      const key = `${lineIndex}-${spanIndex}`
      if (span.className === undefined) {
        return <React.Fragment key={key}>{span.text}</React.Fragment>
      }

      return (
        <span className={span.className} key={key}>
          {span.text}
        </span>
      )
    })
  )
}
